import React, { useState, useEffect } from "react";
import { Table, Thead, Tr, Th, Sortable } from "@lightspeed/cirrus-table";
import { Card } from "@lightspeed/flame/Card";
import Row from "./paymentTableDeferred/Row";
import Loader from "../Loader";
import { useSelector } from "react-redux";
import * as sortFns from "../../utils/sortFn";
import ClassDepLocInfo from "./ClassDepLocInfo";
import { normalizeQuickBooksAccountList, normalizeQuickBooksClassDepartmentList } from "../../utils/quickbooksClassDepartment";
import { isAcombagoIntegration } from "../../utils/d365Dimensions";

const PaymentTable = ({ data, scrollPos, sortState, changeSortState }) => {
  const ui = useSelector((state) => state.ui);
  const database = useSelector((state) => state.database);

  const [sortedData, setSortedData] = useState([...data]);
  const [accountsList, setAccountsList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [classList, setClassList] = useState({ classes: [], departments: [] });
  const [loading, setLoading] = useState(true);

  const sortValues = ["unsorted", "desc", "asc"];
  const reducer = (accumulator, currentValue) => accumulator + currentValue;
  const acombagoIntegration = isAcombagoIntegration(database.currentCompany, database.currentShop);
  const accountingColsCount = acombagoIntegration ? 6 : 7;
  const quickBooksDropShopId =
    database.currentCompany?.type === "RESTOK"
      ? database.currentShop?.area_id ?? database.currentShop?.shop_id ?? 0
      : database.currentShop?.shop_id ?? 0;

  const normalizeCustomers = (rows) =>
    Array.isArray(rows)
      ? rows
          .map((row) => {
            const id =
              row?.id ??
              row?.customer_id ??
              row?.qb_customer_id ??
              row?.customerid ??
              row?.customer_number ??
              row?.value ??
              "";
            const description =
              row?.description ??
              row?.customer_name ??
              row?.name ??
              row?.display_name ??
              row?.fully_qualified_name ??
              row?.label ??
              id;

            return {
              ...row,
              id: String(id).trim(),
              description: String(description ?? "").trim(),
            };
          })
          .filter((row) => row.id || row.description)
      : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accRes, classRes, customerRes] = await Promise.all([
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_ACCOUNT_LIST",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_CLASSDEPARTMENT",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_CUSTOMER_LIST",
            }),
          }),
        ]);

        const accountsData = await accRes.json();
        const classDataRaw = await classRes.json();
        const customerDataRaw = await customerRes.json();

        setAccountsList(normalizeQuickBooksAccountList(accountsData));
        setCustomersList(normalizeCustomers(customerDataRaw));

        const cleanedClassList = normalizeQuickBooksClassDepartmentList(classDataRaw);

        setClassList(cleanedClassList);
        setLoading(false);
      } catch (err) {
        console.error("❌ Greška pri fetchanju podataka:", err);
        setLoading(false);
      }
    };

    if (database?.currentCompany?.id && quickBooksDropShopId) {
      fetchData();
    }
  }, [database.currentCompany.id, quickBooksDropShopId]);

  useEffect(() => {
    setSortedData([...data]);
  }, [data]);

  useEffect(() => {
    if (sortState.reduce(reducer) > 0) {
      let newData = [...data];

      if (sortState[0] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("company_id"))];
      } else if (sortState[0] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("company_id")).reverse()];
      }

      if (sortState[1] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("paymenttype_id"))];
      } else if (sortState[1] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("paymenttype_id")).reverse()];
      }

      if (sortState[2] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("paymenttypeid_name"))];
      } else if (sortState[2] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("paymenttypeid_name")).reverse()];
      }

      if (sortState[3] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id"))];
      } else if (sortState[3] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id")).reverse()];
      }

      if (sortState[4] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_description"))];
      } else if (sortState[4] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("account_description")).reverse()];
      }

      if (sortState[5] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_id"))];
      } else if (sortState[5] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_id")).reverse()];
      }

      if (sortState[6] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_description"))];
      } else if (sortState[6] === 2) {
        newData = [
          ...newData.sort(sortFns.customSortGenString("over_short_description")).reverse(),
        ];
      }

      setSortedData(newData);
    } else {
      setSortedData([...data]);
    }
  }, [sortState, data]);

  if (sortedData.length === 0 && ui.search) {
    return (
      <div className="loader-no-item">
        <h3>No items match your search</h3>
      </div>
    );
  }

  if (!sortedData || !Array.isArray(sortedData) || loading) {
    return <Loader />;
  }

  return (
    <Card top="true">
      <div className="payment-table-wrap">
        <Table>
          <Thead>
            <Tr className={`section-row ${ui.theme === "flame" ? "" : "dark-table-section"}`}>
              <Th colSpan="3" className="section-title section-title-pos">
                <div className="section-badge">POS</div>
              </Th>
              <Th colSpan={accountingColsCount} className="section-title section-title-acc">
                <div className="section-badge">Accounting</div>
              </Th>
            </Tr>

            {scrollPos === true ? (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table"}`}>
                <Sortable.Th
                  width="10%"
                  direction={sortValues[sortState[0]]}
                  onClick={() => changeSortState(0)}
                  className="th-pos"
                >
                  Company
                </Sortable.Th>

                <Sortable.Th
                  width="15%"
                  direction={sortValues[sortState[1]]}
                  onClick={() => changeSortState(1)}
                  className="th-pos"
                >
                  Payment Id
                </Sortable.Th>

                <Sortable.Th
                  width="20%"
                  direction={sortValues[sortState[2]]}
                  onClick={() => changeSortState(2)}
                  className="th-pos th-pos-last"
                >
                  Payment Name
                </Sortable.Th>

                <Sortable.Th
                  width="20%"
                  direction={sortValues[sortState[3]]}
                  onClick={() => changeSortState(3)}
                  className="th-acc th-acc-first"
                >
                  Account Id
                </Sortable.Th>

                <Sortable.Th
                  width="20%"
                  direction={sortValues[sortState[4]]}
                  onClick={() => changeSortState(4)}
                  className="th-acc"
                >
                  Account Description
                </Sortable.Th>

                {!acombagoIntegration && (


                  <Th width="20%" className="th-acc th-plain">
                  Class/Dep/Loc <ClassDepLocInfo />
                </Th>


                )}
                <Th width="20%" className="th-acc th-plain">
                  {acombagoIntegration ? "Project" : "Customer"}
                </Th>

                <Sortable.Th
                  width="20%"
                  direction={sortValues[sortState[5]]}
                  onClick={() => changeSortState(5)}
                  className="th-acc"
                >
                  Over/Short Id
                </Sortable.Th>

                <Sortable.Th
                  width="20%"
                  direction={sortValues[sortState[6]]}
                  onClick={() => changeSortState(6)}
                  className="th-acc"
                >
                  Over/Short Description
                </Sortable.Th>

                <Th width="10%" textAlign="right" className="th-acc th-plain th-action">
                  Action
                </Th>
              </Tr>
            ) : (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table-fake"}`}>
                <Th className="table-th-sticky th-pos" width="10%">
                  Company
                </Th>

                <Th className="table-th-sticky th-pos" width="15%">
                  Payment Id
                </Th>

                <Th className="table-th-sticky th-pos th-pos-last" width="20%">
                  Payment Name
                </Th>

                <Th className="table-th-sticky th-acc th-acc-first" width="20%">
                  Account Id
                </Th>

                <Th className="table-th-sticky th-acc" width="20%">
                  Account Description
                </Th>

                {!acombagoIntegration && (


                  <Th className="table-th-sticky th-acc th-plain" width="20%">
                  Class/Dep/Loc <ClassDepLocInfo />
                </Th>


                )}
                <Th className="table-th-sticky th-acc th-plain" width="20%">
                  {acombagoIntegration ? "Project" : "Customer"}
                </Th>

                <Th className="table-th-sticky th-acc" width="20%">
                  Over/Short Id
                </Th>

                <Th className="table-th-sticky th-acc" width="20%">
                  Over/Short Description
                </Th>

                <Th
                  className="table-th-sticky th-acc th-plain th-action"
                  width="10%"
                  textAlign="right"
                >
                  Action
                </Th>
              </Tr>
            )}
          </Thead>

          <tbody className="t-body">
            {sortedData.map((item, index) => (
              <Row
                key={item.paymenttype_id}
                item={item}
                accountsList={accountsList}
                classList={classList}
                customersList={customersList}
                hideClassDepartmentColumn={acombagoIntegration || isAcombagoIntegration(item)}
                rowIndex={index}
              />
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default PaymentTable;
